import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of, switchMap, tap, from, mergeMap, first, shareReplay } from 'rxjs';
import { environment } from 'environments/environment';
import { User } from 'app/core/user/user.types';
import { PartnersService } from 'app/shared/services/partners.service';
import { getActorGuid, getActorNumericId } from 'app/shared/utils/actor-identifiers.util';
import { hasPagePermission } from 'app/shared/utils/profile-permissions.util';
import { requiresPartnerSelection } from 'app/shared/utils/sica-route-catalog';
import { hasAvantTravauxAccess, hasCssIndirectAccess } from 'app/shared/utils/partner-sica-profiles.util';

export const DOSSIERS_LINK = '/partners/drop-list';
export const PROJECT_DOSSIERS_LINK = '/partner-dossier-project';

/** Company profiles enable project dossiers; user page grants remain required for each link. */
@Injectable({ providedIn: 'root' })
export class DossierNavigationService {
    private visibleLinks: string[] | null = null;
    private readonly profileChecks = new Map<string, Observable<boolean>>();

    private partnerActors$: Observable<any[]>;

    constructor(private readonly http: HttpClient, private readonly partners: PartnersService) {}

    allowsLink(link: string): boolean {
        const path = link?.toLowerCase();
        return ![DOSSIERS_LINK, PROJECT_DOSSIERS_LINK].includes(path) ||
            this.visibleLinks === null || this.visibleLinks.includes(path);
    }

    refresh(user: User): Observable<void> {
        this.visibleLinks = [];
        this.profileChecks.clear();
        this.partnerActors$ = undefined;
        const profileNames = [...(user?.userProfiles || []),
            ...(user?.user_UserProfiles || []).map(item => item.userProfile)].map(profile => profile?.name?.toLowerCase());
        const admin = profileNames.some(name => ['admin e. partenaire', 'admin espace partenaire'].includes(name));
        const selectedPartner = this.read('currentPartner_localstorage');
        const selectedMandataire = this.read('currentMandataire_localstorage');
        const allPartners = localStorage.getItem('tousPartenaires_localstorage') === 'true';
        if (admin && allPartners && !selectedPartner && !selectedMandataire) {
            this.setVisible(user, [DOSSIERS_LINK, PROJECT_DOSSIERS_LINK]);
            return of(undefined);
        }
        let candidates$: Observable<any[]>;
        if (!requiresPartnerSelection(user)) {
            candidates$ = of([user?.teraActor ?? { id: user?.teraActorId, codeClient: user?.company?.codeClient }]);
        } else if (selectedPartner) {
            candidates$ = of([selectedPartner]);
        } else if (selectedMandataire && !selectedMandataire.isAllMandataires) {
            candidates$ = this.partners.getPartnersByMandate(getActorGuid(selectedMandataire) ?? selectedMandataire.id);
        } else {
            const mandates = this.read('mandatairesOfSuperviseur_localstorage') || [];
            candidates$ = mandates.length
                ? forkJoin(mandates.map(mandate => this.partners.getPartnersByMandate(getActorGuid(mandate) ?? mandate.id)))
                    .pipe(map((lists: any[][]) => lists.flat()))
                : of([]);
        }
        return candidates$.pipe(
            switchMap(candidates => {
                const unique = new Map<string, any>();
                for (const partner of candidates) {
                    if (partner.isActive === false || partner.isDeleted === true) continue;
                    const key = getActorGuid(partner) ?? partner.code ?? partner.codeClient;
                    if (key) unique.set(key, partner);
                }
                return from([...unique.values()]).pipe(
                    mergeMap(partner => this.partnerHasProjectAccess(partner, user), 6),
                    first(hasProject => hasProject, false)
                );
            }),
            tap(hasProjectAccess => this.choose(user, hasProjectAccess)),
            map(() => undefined),
            catchError(error => {
                console.warn('[Dossier navigation] Unable to load partner permissions', {status: error?.status});
                // Do not guess a dossier context when its permission lookup failed.
                this.visibleLinks = [];
                return of(undefined);
            })
        );
    }

    private partnerHasProjectAccess(partner: any, user: User): Observable<boolean> {
        const guid = getActorGuid(partner);
        const code = partner.code ?? partner.codeClient;
        if (!guid && !this.partnerActors$) {
            this.partnerActors$ = this.partners.getActorsByType('AC2').pipe(
                shareReplay({bufferSize: 1, refCount: true})
            );
        }
        const actor$ = guid ? of(partner) : this.partnerActors$.pipe(map(actors => actors.find(actor =>
            code ? (actor.code ?? actor.codeClient)?.toLowerCase() == code.toLowerCase()
                : getActorNumericId(partner) !== null && getActorNumericId(actor) === getActorNumericId(partner)
        )));
        return actor$.pipe(
            switchMap(actor => {
                const id = getActorGuid(actor);
                if (!id) throw new Error('Missing TERA partner GUID for dossier permissions');
                return this.partners.getTeraActorSicaProfiles(id);
            }),
            switchMap(links => {
                const partnerProfiles = (links ?? []).filter(link => link.isActive !== false && !link.isDeleted &&
                    link.userProfile?.isActive !== false && !link.userProfile?.isDeleted)
                    .filter(link => !!link.userProfile?.id && !!link.userProfile?.name)
                    .map(link => ({ id: link.userProfile.id, name: link.userProfile.name }));
   
                const hasEligiblePartnerProfile =
                    hasAvantTravauxAccess(partnerProfiles) || hasCssIndirectAccess(partnerProfiles);

                if (!hasEligiblePartnerProfile) {
                    return of(false);
                }

                const userProfileIds = [...new Set([
                    ...(user?.user_UserProfiles ?? []).map(link => link.userProfileId ?? link.userProfile?.id),
                    ...(user?.userProfiles ?? []).map(profile => profile.id)
                ].filter((id): id is string => !!id))];
                return userProfileIds.length
                    ? forkJoin(userProfileIds.map(id => this.profileHasProjectAccess(id)))
                        .pipe(map(results => results.some(Boolean)))
                    : of(false);
            })
        );
    }

    private profileHasProjectAccess(id: string): Observable<boolean> {
        if (!this.profileChecks.has(id)) {
            this.profileChecks.set(id, this.http.get<Array<{link: string; accessLevel: string[]}>>(
                environment.TERA_RESOURCE_API + environment.teraApiCatalog.userProfilePermission.controllerRoute
                + environment.teraApiCatalog.userProfilePermission.subRoutes.getByProfileId + id + '?grantedOnly=true'
            ).pipe(
                map(list => list.some(permission => permission.link?.toLowerCase() == PROJECT_DOSSIERS_LINK &&
                    permission.accessLevel?.length > 0)),
                shareReplay({bufferSize: 1, refCount: true})
            ));
        }
        return this.profileChecks.get(id);
    }

    private choose(user: User, hasProjectAccess: boolean): void {
        this.setVisible(user, hasProjectAccess ? [DOSSIERS_LINK, PROJECT_DOSSIERS_LINK] : [DOSSIERS_LINK]);
    }

    private setVisible(user: User, links: string[]): void {
        this.visibleLinks = links.filter(link => hasPagePermission(user, link));
    }

    private read(key: string): any {
        try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
    }
}
